import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Side-effect import: invokes Sentry.init() with the credential-scrub beforeSend
// hook (D-18). Must run BEFORE any other code that might throw, so this import
// stays at the top of the import list.
import "./sentry/init";

// Phase 8 BRAND-1.3 fonts are declared inline in web/src/index.css.
// 2026-05-11: both Latin (DIN Next W1G, 5 weights) and Arabic (DIN Next
// Arabic, 5 weights) are commercial Linotype fonts under the municipality's
// Monotype web+app embedding license — see shared/brand/source/DINNext-
// LICENSE.txt. No @fontsource npm package; everything is self-hosted from
// /brand/. NO Google Fonts CDN (V-M5 light-security posture,
// NFR-VIZ-OFFLINE-1).

import { App } from "./App";
import "./index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root element not found in index.html");

// Phase 9 Wave 3 — dev-mode-only collision fixture branch. Triggered by
// /?fixture=chrome-vs-pin-collision in dev builds; production tree-shakes the
// dynamic import via import.meta.env.DEV guard. The fixture renders the
// chrome-vs-pin-collision visual regression scene (DimensionPillToggleGroup +
// AssetSidePanelChrome mock + 4 pin status circles) consumed by
// web/tests/visual/chrome-vs-pin-collision.spec.ts (V-CP1 integrated probe).
//
// Phase 11 Plan 11-02 — second fixture branch added at
// /?fixture=asset-side-panel-long-history for the sticky-footer Playwright
// spec (D-19, MAPCHROME-4.5). Same dev-only tree-shake pattern.
//
// Phase 11 Plan 11-06 — third fixture branch at
// /?fixture=integrated-map-chrome for the pin-recolor perf gate
// measurement (D-02 per-surface go/no-go, NFR-VIZ-PERF-1, MAPCHROME-4.6).
// Renders the live MapView with a pre-seeded 2,500-feature TanStack Query
// cache + production-mirroring TopAppBar so blur cost is measured against
// the real recolor hot path. Per-surface blur isolation via the `blur=`
// query param.
//
// Phase 13 Plan 13-02 — fourth fixture branch at /?fixture=photostrip&kinds=...
// for Plan 13-04 Playwright probes (photostrip-states.spec.ts +
// photostrip-rtl-photo-not-flipped.spec.ts). Renders all 4 PhotoTile kinds
// (landed/uploading/broken/local-only) for V-CP3 RTL invariant + V-M3
// assertImageRendered + dual-locale visual baseline consumption. Same
// dev-only tree-shake pattern via import.meta.env.DEV.
//
// Phase 14 Plan 14-03a — fifth fixture branch at
// /?fixture=asset-side-panel-5-inspections-latest-expanded for the V1
// information-density Playwright spec (asset-side-panel-density.spec.ts,
// D-10, DENSITY-7.2) and V2 viewport-stability spec
// (asset-side-panel-mobile-viewport.spec.ts, D-11, DENSITY-7.2).
// Seeds 5 inspections (the "typical-workflow" shape per operator brief);
// HistorySection's default-expand-latest-row behavior (commit 5c19010)
// auto-expands the latest row without any fixture-side flag. Same dev-only
// tree-shake pattern via import.meta.env.DEV.
//
// Reference: 09-03-PLAN.md Task 1 Step F (BL-8 entry-point verification)
// Reference: 11-02-PLAN.md Task 3 Edit 4 (sticky-footer fixture wiring)
// Reference: 11-06-PLAN.md Task 1 (per-surface fixture wiring)
// Reference: 13-02-PLAN.md Task 3 (PhotoStrip fixture wiring)
// Reference: 14-03a-PLAN.md Task 3a.1 (density + viewport fixture wiring)
const params = new URLSearchParams(window.location.search);
const fixtureName = import.meta.env.DEV ? params.get("fixture") : null;
if (fixtureName === "chrome-vs-pin-collision") {
  void import("./dev/CollisionFixturePage").then(({ CollisionFixturePage }) => {
    createRoot(rootEl).render(
      <StrictMode>
        <CollisionFixturePage />
      </StrictMode>,
    );
  });
} else if (fixtureName === "asset-side-panel-long-history") {
  void import("./dev/AssetSidePanelLongHistoryFixture").then(
    ({ AssetSidePanelLongHistoryFixture }) => {
      createRoot(rootEl).render(
        <StrictMode>
          <AssetSidePanelLongHistoryFixture />
        </StrictMode>,
      );
    },
  );
} else if (fixtureName === "integrated-map-chrome") {
  void import("./dev/IntegratedMapChromeFixture").then(
    ({ IntegratedMapChromeFixture }) => {
      createRoot(rootEl).render(
        <StrictMode>
          <IntegratedMapChromeFixture />
        </StrictMode>,
      );
    },
  );
} else if (fixtureName === "photostrip") {
  // Phase 15 D-13 — extended with &lightbox=1 sub-param (handled inside
  // photostrip-fixture.tsx, not at this routing level). When set, the fixture
  // renders <PhotoLightbox> over the PhotoStrip; consumed by
  // web/tests/visual/photostrip-lightbox-open.spec.ts.
  void import("./dev/photostrip-fixture").then(
    ({ PhotoStripFixturePage }) => {
      createRoot(rootEl).render(
        <StrictMode>
          <PhotoStripFixturePage />
        </StrictMode>,
      );
    },
  );
} else if (fixtureName === "asset-side-panel-5-inspections-latest-expanded") {
  // Phase 14 V1 + V2 density spec fixture — Wave 3a (DENSITY-7.2)
  void import("./dev/AssetSidePanel5InspectionsLatestExpandedFixture").then(
    ({ AssetSidePanel5InspectionsLatestExpandedFixture }) => {
      createRoot(rootEl).render(
        <StrictMode>
          <AssetSidePanel5InspectionsLatestExpandedFixture />
        </StrictMode>,
      );
    },
  );
} else {
  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </StrictMode>,
  );
}
